// Server functions de lectura pública de eventos, con caché KV (fallback memoria).
//
// IMPORTANTE: la caché se aplica solo a datos públicos no críticos (catálogo
// y meta de evento). El conteo de disponibilidad en tiempo real y el stock
// usado por checkout sigue leyéndose en vivo desde Postgres (primary) vía
// los flujos existentes (create-ticket-checkout, create-hold).
//
// TTLs: listado 60s, detalle 120s. La purga es explícita desde create/update/
// cancel/delete event (ver events.functions.ts).

import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const TTL_LIST = 60;
const TTL_DETAIL = 120;

export const getPublicEventsList = createServerFn({ method: "GET" }).handler(async () => {
  const { cacheGetOrSet } = await import("./cache.server");
  return cacheGetOrSet("evt:list:v1:all", TTL_LIST, async () => {
    // Lectura pública tolerante a replica lag → dbRead (replica si está provisionada).
    const { dbRead } = await import("./db.server");
    const { data, error } = await dbRead
      .from("events")
      .select("*, ticket_types(*)")
      .eq("is_published", true)
      .is("deleted_at", null)
      .order("date", { ascending: true });
    if (error) throw new Error(error.message);
    return data ?? [];
  });
});

export const getPublicEventDetail = createServerFn({ method: "POST" })
  .inputValidator((d: { eventId: string }) =>
    z.object({ eventId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const { cacheGetOrSet } = await import("./cache.server");
    return cacheGetOrSet(`evt:${data.eventId}:v1`, TTL_DETAIL, async () => {
      const { dbRead } = await import("./db.server");
      const { data: ev } = await dbRead
        .from("events")
        .select(
          "id, title, description, date, time, location, city, image_url, category, ticket_types(price, currency)",
        )
        .eq("id", data.eventId)
        .eq("is_published", true)
        .is("deleted_at", null)
        .maybeSingle();
      return ev;
    });
  });

/** Purga manual (admin/debug). Las mutaciones de evento ya purgan automáticamente. */
export const purgeEventCache = createServerFn({ method: "POST" })
  .inputValidator((d: { eventId: string }) =>
    z.object({ eventId: z.string().uuid() }).parse(d),
  )
  .handler(async ({ data }) => {
    const { cachePurge, eventCacheKeys } = await import("./cache.server");
    await cachePurge(eventCacheKeys(data.eventId));
    return { ok: true };
  });
